import {
    auditAiConfig,
    useGetAttributeCategoryKeysQuery,
    useLazyGetAttributeCategoryKeysQuery,
} from "@callveriq/redux/services/settings/auditAiConfig.service";
import { isDefined } from "@callveriq/utils/helper/common.helper";
import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";

/**
 * @description a custom hook for fetching all the atteribute category keys based on the the param
 *
 * @export
 * @param {{
 *         query?: string;
 *         metadata_type?: number;
 *         data_type?: string;
 *         id?: number;
 *     }} payload
 * @param {boolean} [fetchAll]
 * @param {{
 *         skip?: boolean;
 *         refetchOnMountOrArgChange?: boolean;
 *     }} [extras]
 * @return {*}
 */
export function useGetAttributeCategoryKeysList(
    payload: {
        query?: string;
        metadata_type?: number;
        data_type?: string;
        id?: number;
    },
    fetchAll?: boolean,
    extras?: {
        skip?: boolean;
        refetchOnMountOrArgChange?: boolean;
    }
    // if `true`: auto do next fetches to get all notes at once
) {
    // baseResult is for example
    const baseResult = useGetAttributeCategoryKeysQuery(payload, {
        ...extras,
    });
    // trigger may be fired many times
    const [trigger, nextResult] = useLazyGetAttributeCategoryKeysQuery();
    const dispatch = useDispatch();
    const isBaseReady = useRef(false);
    const isNextDone = useRef(true);
    // next: starts with a null, fetching ended with an null , It stores a url
    const next = useRef<null | string>(null);
    const abortControllerRef = useRef<ReturnType<typeof trigger> | null>(null);

    // Function to reset state
    const resetState = () => {
        isBaseReady.current = false;
        isNextDone.current = true;
        next.current = null;
    };

    // Base result
    useEffect(() => {
        if (!baseResult.data) {
            return;
        }
        next.current = baseResult.data?.next;
        if (baseResult.data) {
            isBaseReady.current = true;
            fetchAll && fetchNext();
        }
    }, [baseResult]);

    // When there comes a next fetched result
    useEffect(() => {
        if (nextResult.isFetching) return;
        abortControllerRef.current = null;
        if (!nextResult.isSuccess) {
            resetState();
            return;
        }
        if (
            isBaseReady.current &&
            nextResult.data &&
            nextResult.data.next !== next.current
        ) {
            next.current = nextResult.data.next; // undefined if no data further

            // Put next fetched snippets into the first queried collection (as a base collection)
            // This can help us do optimistic/pessimistic updates against the base collection
            const newItems = nextResult.data?.results || [];
            dispatch(
                auditAiConfig.util.updateQueryData(
                    "getAttributeCategoryKeys",
                    payload,
                    (drafts) => {
                        drafts.next = nextResult.data.next;
                        drafts.previous = nextResult.data.previous;
                        if (newItems && newItems.length > 0) {
                            // depends on the use case,
                            // maybe we can do deduplication, removal of some old entries here, if required
                            // ...

                            // adding new snippets to the cache
                            drafts.results.push(...newItems);
                        }
                    }
                )
            );
        }
    }, [nextResult]);

    const fetchNext = async () => {
        if (
            !isBaseReady.current ||
            !isNextDone.current ||
            !isDefined(next.current)
        )
            return;

        try {
            isNextDone.current = false;
            const result = trigger({
                ...payload,
                next: next.current,
            });
            abortControllerRef.current = result;
        } catch (e) {
            console.error("Fetch next error:", e);
        } finally {
            isNextDone.current = true;
            fetchAll && fetchNext();
        }
    };

    const refetch = async () => {
        resetState();
        await baseResult.refetch(); // restart with a whole new refetching
    };

    // Reset everything when payload changes
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }
        };
    }, [payload.metadata_type]);

    return {
        data: baseResult.data,
        error: baseResult.error,
        isError: baseResult.isError,
        isLoading: baseResult.isLoading,
        isFetching: baseResult.isFetching || nextResult.isFetching,
        errorNext: nextResult.error,
        isErrorNext: nextResult.isError,
        isFetchingNext: nextResult.isFetching,
        hasNext: baseResult.data?.next !== null,
        fetchNext,
        refetch,
    };
}
